import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { sitesApi, type Site, type CreateSiteResponse } from '../api/client';

export default function Sites() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSiteData, setNewSiteData] = useState<CreateSiteResponse['site'] | null>(null);

  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['sites'],
    queryFn: sitesApi.list,
  });

  const createMutation = useMutation({
    mutationFn: sitesApi.create,
    onSuccess: (data) => {
      setNewSiteData(data.site);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: sitesApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  const sites = data?.sites || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Sites</h1>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
          Add Site
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">
          Failed to load sites: {error.message}
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading sites...</div>
      ) : sites.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 mb-4">No sites configured yet</p>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            Add Your First Site
          </button>
        </div>
      ) : (
        <div className="grid gap-4">
          {sites.map((site) => (
            <SiteCard
              key={site.id}
              site={site}
              onDelete={() => {
                if (confirm(`Delete ${site.domain}? This will also delete all widgets.`)) {
                  deleteMutation.mutate(site.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Add Site Modal */}
      {showAddModal && (
        <AddSiteModal
          onClose={() => {
            setShowAddModal(false);
            setNewSiteData(null);
          }}
          onSubmit={(data) => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
          newSiteData={newSiteData}
        />
      )}
    </div>
  );
}

function SiteCard({ site, onDelete }: { site: Site; onDelete: () => void }) {
  return (
    <div className="card flex items-center justify-between">
      <div>
        <Link
          to={`/sites/${site.id}`}
          className="text-lg font-semibold text-gray-900 hover:text-primary-600"
        >
          {site.domain}
        </Link>
        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
          <span>Added {new Date(site.created_at).toLocaleDateString()}</span>
          {site.has_wp_password && (
            <span className="inline-flex items-center gap-1 text-green-600">
              ✓ WordPress Connected
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Link to={`/sites/${site.id}`} className="btn btn-secondary">
          Manage
        </Link>
        <button className="btn btn-danger" onClick={onDelete}>
          Delete
        </button>
      </div>
    </div>
  );
}

function AddSiteModal({
  onClose,
  onSubmit,
  isLoading,
  newSiteData,
}: {
  onClose: () => void;
  onSubmit: (data: { domain: string; wp_username?: string; wp_app_password?: string }) => void;
  isLoading: boolean;
  newSiteData: CreateSiteResponse['site'] | null;
}) {
  const [domain, setDomain] = useState('');
  const [wpUsername, setWpUsername] = useState('');
  const [wpPassword, setWpPassword] = useState('');

  // Show success state with API key
  if (newSiteData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Site Added Successfully!</h2>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800 mb-2">
              <strong>Important:</strong> Save this API key now. It won't be shown again.
            </p>
            <code className="block bg-green-100 p-2 rounded text-sm break-all">
              {newSiteData.api_key}
            </code>
          </div>

          <div className="space-y-3 mb-6">
            <div>
              <span className="text-sm text-gray-500">Domain:</span>
              <p className="font-medium">{newSiteData.domain}</p>
            </div>
            <div>
              <span className="text-sm text-gray-500">Webhook URL:</span>
              <code className="block bg-gray-100 p-2 rounded text-sm break-all mt-1">
                {newSiteData.webhook_url}
              </code>
            </div>
          </div>

          <button className="btn btn-primary w-full" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Add New Site</h2>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({
              domain,
              wp_username: wpUsername || undefined,
              wp_app_password: wpPassword || undefined,
            });
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="label">Domain *</label>
              <input
                type="text"
                className="input"
                placeholder="example.com"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                required
              />
            </div>

            <hr className="my-4" />
            <p className="text-sm text-gray-500">
              Optional: Add WordPress credentials to enable automatic content fetching
            </p>

            <div>
              <label className="label">WordPress Username</label>
              <input
                type="text"
                className="input"
                placeholder="admin"
                value={wpUsername}
                onChange={(e) => setWpUsername(e.target.value)}
              />
            </div>

            <div>
              <label className="label">Application Password</label>
              <input
                type="password"
                className="input"
                placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                value={wpPassword}
                onChange={(e) => setWpPassword(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Generate in WordPress: Users → Your Profile → Application Passwords
              </p>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button type="button" className="btn btn-secondary flex-1" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary flex-1" disabled={isLoading}>
              {isLoading ? 'Adding...' : 'Add Site'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
